import { httpClient } from './httpClient';
import {
  AccountBrand,
  Brand,
  BrandsListFilters,
  CreateBrandData,
  UpdateBrandData,
  ApiResponse,
} from '@/types/accountBrands';

/**
 * Account Brands Service
 * Handles all API calls to /api/v2/account-brands
 */
class AccountBrandsService {
  private static instance: AccountBrandsService;
  private readonly baseUrl = '/api/v2/account-brands';

  static getInstance(): AccountBrandsService {
    if (!AccountBrandsService.instance) {
      AccountBrandsService.instance = new AccountBrandsService();
    }
    return AccountBrandsService.instance;
  }

  /**
   * Get list of brands with optional filters
   */
  async getBrands(filters?: BrandsListFilters): Promise<AccountBrand[]> {
    try {
      const params: Record<string, string | number> = {};

      if (filters?.account_id) {
        params.account_id = filters.account_id;
      }
      if (filters?.is_active !== undefined) {
        params.is_active = filters.is_active.toString();
      }
      if (filters?.search) {
        params.search = filters.search;
      }

      const response = await httpClient.get<ApiResponse<AccountBrand[]> | AccountBrand[]>(
        this.baseUrl,
        { params }
      );

      // Handle both response formats
      // Format 1: { success, data } wrapper
      if (typeof response === 'object' && response !== null && 'success' in response) {
        if (!response.success) {
          throw new Error(response.message || 'Failed to fetch brands');
        }
        return response.data;
      }

      // Format 2: Direct array response
      if (Array.isArray(response)) {
        return response;
      }

      // Fallback for unexpected format
      console.error('[AccountBrandsService] Unexpected response format:', response);
      return [];
    } catch (error) {
      console.error('[AccountBrandsService] Error fetching brands:', error);
      throw error;
    }
  }

  /**
   * Get a specific brand by ID
   */
  async getBrand(id: number): Promise<AccountBrand> {
    try {
      const response = await httpClient.get<ApiResponse<AccountBrand>>(`${this.baseUrl}/${id}`);

      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch brand');
      }

      return response.data;
    } catch (error) {
      console.error(`[AccountBrandsService] Error fetching brand ${id}:`, error);
      throw error;
    }
  }

  /**
   * Get available brands for selection (autocomplete/dropdown)
   */
  async getAvailableBrands(search?: string): Promise<Brand[]> {
    try {
      const params: Record<string, string> = {};
      if (search) {
        params.search = search;
      }

      const response = await httpClient.get<ApiResponse<Brand[]>>(
        `${this.baseUrl}/available-brands`,
        { params }
      );

      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch available brands');
      }

      return response.data;
    } catch (error) {
      console.error('[AccountBrandsService] Error fetching available brands:', error);
      throw error;
    }
  }

  /**
   * Create a new brand
   */
  async createBrand(data: CreateBrandData): Promise<AccountBrand> {
    try {
      // Validation
      if (!data.account_id) {
        throw new Error('account_id is required');
      }

      if (!data.brand_id && !data.name) {
        throw new Error('Either brand_id or name is required');
      }

      if (data.brand_id && data.name) {
        throw new Error('Provide either brand_id OR name, not both');
      }

      const response = await httpClient.post<ApiResponse<AccountBrand>>(this.baseUrl, data);

      if (!response.success) {
        throw new Error(response.message || 'Failed to create brand');
      }

      return response.data;
    } catch (error) {
      console.error('[AccountBrandsService] Error creating brand:', error);
      throw error;
    }
  }

  /**
   * Update an existing brand
   */
  async updateBrand(id: number, data: UpdateBrandData): Promise<AccountBrand> {
    try {
      // Validation
      if (data.is_active === undefined) {
        throw new Error('is_active is required for updates');
      }

      const response = await httpClient.put<ApiResponse<AccountBrand>>(`${this.baseUrl}/${id}`, data);

      if (!response.success) {
        throw new Error(response.message || 'Failed to update brand');
      }

      return response.data;
    } catch (error) {
      console.error(`[AccountBrandsService] Error updating brand ${id}:`, error);
      throw error;
    }
  }

  /**
   * Delete a brand
   */
  async deleteBrand(id: number): Promise<void> {
    try {
      const response = await httpClient.delete<ApiResponse<void>>(`${this.baseUrl}/${id}`);

      if (!response.success) {
        throw new Error(response.message || 'Failed to delete brand');
      }
    } catch (error) {
      console.error(`[AccountBrandsService] Error deleting brand ${id}:`, error);
      throw error;
    }
  }

  /**
   * Get account brands performance (for My Brands Performance table)
   */
  async getPerformance(filters: {
    account_id?: number;
    startDate: string;
    endDate: string;
    channels: string;
    segments?: string;
    [key: string]: any; // Allow additional demographic filters
  }): Promise<any> {
    try {
      const params: Record<string, string | number> = {};

      // Add all filters to params
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params[key] = typeof value === 'number' ? value : value.toString();
        }
      });

      const response = await httpClient.get<any>(
        `${this.baseUrl}/performance`,
        { params }
      );

      // Note: This endpoint returns data directly (no "success" wrapper)
      return response;
    } catch (error) {
      console.error('[AccountBrandsService] Error fetching account brands performance:', error);
      throw error;
    }
  }
}

export const accountBrandsService = AccountBrandsService.getInstance();
